import { useEffect, useRef, useState } from 'react'
import { CallBackProps, Placement, STATUS } from 'react-joyride'
import { useLocation } from 'react-router-dom'
import { useIsFetching } from '@tanstack/react-query'
import { doesTourApplyToRoute, waitForElement } from '../common/helper'
import useUpdateWhatsNewTour from '../domain/useUpdateWhatsNewTour'
import { ApiTour, ApiTourStep } from '../types/tourResponse'
import JoyrideTooltip from '../components/WhatsNew'
import { trackEvent } from '../common/fireflyHelper'

const joyrideStyles = {
  options: {
    // Used a high zIndex so the tour overlay and tooltip sit above side panels and modal overlays.
    zIndex: 99999,
  },
  tooltip: {
    borderRadius: 12,
    padding: 16,
  },
}

interface CombinedStep {
  target: string
  title: string
  content: string
  placement: Placement
  action_allowed: boolean
  disableBeacon: boolean
  _sourceComponentId: string
  _sourceStepIndex: number
}

const computePerTourProgress = (
  combinedSteps: CombinedStep[],
  globalStepsCompleted: number,
  sourceTours: ApiTour[],
) => {
  const completedPerTour: Record<string, number> = {}
  const totalPerTour: Record<string, number> = {}

  combinedSteps.forEach((step) => {
    const id = step._sourceComponentId
    totalPerTour[id] = (totalPerTour[id] || 0) + 1
  })

  combinedSteps.forEach((step, i) => {
    if (i < globalStepsCompleted) {
      const id = step._sourceComponentId
      completedPerTour[id] = (completedPerTour[id] || 0) + 1
    }
  })

  return sourceTours
    .filter((tour) => {
      const completed = completedPerTour[tour.component_id] || 0
      return completed > 0
    })
    .map((tour) => {
      const id = tour.component_id
      const completed = completedPerTour[id] || 0
      const total = totalPerTour[id] || 0
      const currentStep = Math.max(1, Math.min(total, completed))
      const isDismissed = completed >= total

      return {
        component_id: id,
        current_step: currentStep,
        is_dismissed: isDismissed,
        skip_count: isDismissed ? tour.skip_count : tour.skip_count + 1,
      }
    })
}

export const useGuidedTour = (tours: ApiTour[], onComplete?: () => void) => {
  const location = useLocation()
  const { mutateAsync: updateWhatsNewTour } = useUpdateWhatsNewTour()
  const isFetching = useIsFetching()

  const [run, setRun] = useState(false)
  const [steps, setSteps] = useState<CombinedStep[]>([])
  const [stepIndex, setStepIndex] = useState(0)
  const [stepsReady, setStepsReady] = useState(false)
  const [activeSourceTours, setActiveSourceTours] = useState<ApiTour[]>([])

  const activeTourKeyRef = useRef<string | null>(null)
  const totalStepsRef = useRef(0)
  const dismissedToursRef = useRef<Set<string>>(new Set())
  const hasExplicitCloseRef = useRef(false)
  const onCompleteRef = useRef(onComplete)
  const runRef = useRef(run)
  const stepIndexRef = useRef(stepIndex)
  const stepsRef = useRef<CombinedStep[]>(steps)
  const activeSourceToursRef = useRef<ApiTour[]>(activeSourceTours)

  useEffect(() => {
    if (!tours.length) return
    const abortController = new AbortController()
    const now = new Date()

    const eligibleTours = tours.filter(
      (tour) =>
        !dismissedToursRef.current.has(tour.component_id) &&
        new Date(tour.end_date) > now &&
        doesTourApplyToRoute(tour.page, location.pathname),
    )

    if (!eligibleTours.length) return

    const toursByPage: Record<string, ApiTour[]> = {}
    eligibleTours.forEach((tour) => {
      const page = tour.page
      if (!toursByPage[page]) {
        toursByPage[page] = []
      }
      toursByPage[page].push(tour)
    })

    const matchingTours = Object.values(toursByPage)[0] || []

    if (!matchingTours.length) return

    matchingTours.sort(
      (a, b) =>
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
    )

    const combinedKey = matchingTours
      .map((tour) => tour.component_id)
      .sort()
      .join('|')
    if (activeTourKeyRef.current === combinedKey) return

    activeTourKeyRef.current = combinedKey
    ;(async () => {
      const allMappedSteps: CombinedStep[] = []

      for (const tour of matchingTours) {
        const mappedSteps = tour.steps.map((step: ApiTourStep) => ({
          target: `#${step.target_id}`,
          title: step.title,
          content: step.body,
          placement: step.side,
          action_allowed: step.action_allowed,
          disableBeacon: true,
          _sourceComponentId: tour.component_id,
          _sourceStepIndex: step.step_index,
        }))

        allMappedSteps.push(...mappedSteps)
      }

      let globalResumeIndex = 0
      let offset = 0
      for (const tour of matchingTours) {
        if (tour.current_step > 1 && !tour.is_dismissed) {
          const tourResumeIndex = Math.max(0, tour.current_step - 1)
          globalResumeIndex = offset + tourResumeIndex
          break
        }
        offset += tour.steps.length
      }

      if (!allMappedSteps.length) {
        activeTourKeyRef.current = null
        return
      }

      const firstStep = allMappedSteps[globalResumeIndex] || allMappedSteps[0]
      const targetId = firstStep.target.replace('#', '')

      try {
        await waitForElement(targetId, undefined, abortController.signal)
      } catch {
        activeTourKeyRef.current = null
        return
      }

      await document.fonts.ready
      totalStepsRef.current = allMappedSteps.length

      hasExplicitCloseRef.current = false
      setActiveSourceTours(matchingTours)
      setSteps(allMappedSteps)
      setStepIndex(globalResumeIndex)
      setStepsReady(true)
    })()

    return () => {
      abortController.abort()
    }
  }, [tours, location.pathname])

  useEffect(() => {
    if (stepsReady && isFetching === 0) {
      setStepsReady(false)
      setRun(true)
    }
  }, [stepsReady, isFetching])

  const handleCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data
    if (type === 'step:after' && action === 'next') {
      const leavingStep = steps[index]
      if (leavingStep) {
        window.dispatchEvent(
          new CustomEvent('guided-tour-step-leave', {
            detail: { target: leavingStep.target },
          }),
        )
      }

      const nextIndex = index + 1
      if (nextIndex >= totalStepsRef.current) {
        // User clicked Next on the last step
        closeTour('button', totalStepsRef.current || nextIndex)
        return
      }

      setRun(false)
      requestAnimationFrame(() => {
        setStepIndex(nextIndex)
        setRun(true)
      })
    }
    if (action === 'skip') {
      // User clicked Skip on the current step
      closeTour('button', index + 1)
      return
    }
    if (action === 'close' || status === STATUS.SKIPPED) {
      // User closed via backdrop/close icon or Joyride reported a skipped tour
      closeTour('backdrop', index + 1)
    }
  }

  useEffect(() => {
    const handleAdvance = () => {
      if (!run) return

      setStepIndex((current) => {
        const next = current + 1
        if (next >= totalStepsRef.current) {
          return current
        }
        return next
      })
    }

    window.addEventListener('guided-tour-advance', handleAdvance)

    return () => {
      window.removeEventListener('guided-tour-advance', handleAdvance)
    }
  }, [run])

  const closeTour = (
    reason: 'button' | 'backdrop' = 'button',
    stepsCompletedOverride?: number,
  ) => {
    hasExplicitCloseRef.current = true
    setRun(false)
    activeTourKeyRef.current = null

    if (!activeSourceTours.length || !steps.length) return

    const globalStepsCompleted =
      stepsCompletedOverride ??
      Math.max(1, Math.min(totalStepsRef.current, stepIndex + 1))

    const isGlobalLastStep = globalStepsCompleted >= totalStepsRef.current

    const perTourProgress = computePerTourProgress(
      steps,
      globalStepsCompleted,
      activeSourceTours,
    )

    perTourProgress.forEach((tourProgress) => {
      dismissedToursRef.current.add(tourProgress.component_id)

      trackEvent(
        tourProgress.is_dismissed
          ? 'WhatsNewTour- Tour completed'
          : 'WhatsNewTour- Tour skipped midway',
        {
          componentId: tourProgress.component_id,
          reason,
          stepsCompleted: tourProgress.current_step,
          combinedTour: true,
          globalStepsCompleted,
          isGlobalLastStep,
        },
      )

      updateWhatsNewTour({
        component_id: tourProgress.component_id,
        current_step: tourProgress.current_step,
        skip_count: tourProgress.skip_count,
        is_dismissed: tourProgress.is_dismissed,
      })
    })

    onCompleteRef.current?.()
  }

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    runRef.current = run
    stepIndexRef.current = stepIndex
    stepsRef.current = steps
    activeSourceToursRef.current = activeSourceTours
  }, [run, stepIndex, steps, activeSourceTours])

  useEffect(() => {
    const handleSkipOnUnloadOrUnmount = () => {
      if (!runRef.current || hasExplicitCloseRef.current) return

      const currentSteps = stepsRef.current
      const currentSourceTours = activeSourceToursRef.current
      if (!currentSourceTours.length || !currentSteps.length) return

      const globalStepsCompleted = Math.max(
        1,
        Math.min(totalStepsRef.current, stepIndexRef.current + 1),
      )

      const perTourProgress = computePerTourProgress(
        currentSteps,
        globalStepsCompleted,
        currentSourceTours,
      )

      perTourProgress.forEach((tourProgress) => {
        updateWhatsNewTour({
          component_id: tourProgress.component_id,
          current_step: tourProgress.current_step,
          skip_count: tourProgress.skip_count,
          is_dismissed: tourProgress.is_dismissed,
        })
      })
    }

    window.addEventListener('beforeunload', handleSkipOnUnloadOrUnmount)

    return () => {
      window.removeEventListener('beforeunload', handleSkipOnUnloadOrUnmount)

      handleSkipOnUnloadOrUnmount()
    }
  }, [updateWhatsNewTour])

  return {
    joyrideProps: {
      run,
      steps,
      stepIndex,
      spotlightClicks: steps[stepIndex]?.action_allowed === false,
      continuous: true,
      showNextButton: true,
      showBackButton: false,
      showSkipButton: true,
      showProgress: false,
      locale: {
        back: 'Back',
        close: 'Close',
        last: 'Close',
        next: 'Next',
        skip: 'Close',
      },
      disableOverlayClose: false,
      disableCloseOnEsc: false,
      disableScrolling: true,
      scrollToFirstStep: false,
      callback: handleCallback,
      styles: joyrideStyles,
      tooltipComponent: JoyrideTooltip,
    },
  }
}
